/**
 * InlineDashboard Component
 * 
 * 헤더 플랜 row 아래에 펼쳐지는 인라인 대시보드
 * 핵심 플랜 정보와 사용량을 간략하게 표시
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { getHistory } from '../api';
import type { QuotaInfo } from '../api';

interface InlineDashboardProps {
  onUpgrade: () => void;
  onClose: () => void;
}

export default function InlineDashboard({ onUpgrade, onClose }: InlineDashboardProps) {
  const { quota, isAuthenticated } = useAuth();
  const { t, lang } = useLanguage();
  const [historyUsage, setHistoryUsage] = useState<{ used: number; limit: number } | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // #region agent log
  if (quota) fetch('http://127.0.0.1:7243/ingest/c2fcca66-bf74-4cf9-b7f9-e16e46cf098e',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'InlineDashboard.tsx:render',message:'InlineDashboard received quota',data:{plan:quota.plan,limits:quota.limits,max_test_cases:quota.limits.max_test_cases,max_planning_chars:quota.limits.max_planning_chars,history_limit:quota.limits.history_limit},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'C'})}).catch(()=>{});
  // #endregion

  // 히스토리 사용량 로드
  useEffect(() => {
    if (isAuthenticated && quota && quota.limits.history_limit > 0) {
      setIsLoadingHistory(true);
      getHistory()
        .then((data) => {
          // quota.limits.history_limit 사용 (DevPlanSwitcher 오버라이드 반영)
          setHistoryUsage({ used: data.total, limit: quota.limits.history_limit });
        })
        .catch(() => {
          setHistoryUsage({ used: 0, limit: quota.limits.history_limit });
        })
        .finally(() => {
          setIsLoadingHistory(false);
        });
    }
  }, [isAuthenticated, quota]);
  
  if (!isAuthenticated || !quota) {
    return null;
  }
  
  const { monthly_evaluations, limits, team } = quota as QuotaInfo;
  const { used, limit, unlimited } = monthly_evaluations;
  
  // 사용률 계산
  const usagePercent = unlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !unlimited && used >= limit * 0.8;
  const isAtLimit = !unlimited && used >= limit;
  
  // 팀 사용 여부
  const isTeamPlan = team !== null && team !== undefined;
  
  // 플랜 색상
  const planColors: Record<string, { bg: string; border: string }> = {
    free: { bg: 'bg-[var(--neo-blue)]/20', border: 'border-[var(--neo-blue)]' },
    pro: { bg: 'bg-[var(--neo-mint)]/20', border: 'border-[var(--neo-mint)]' },
    max: { bg: 'bg-[var(--neo-orange)]/20', border: 'border-[var(--neo-orange)]' },
    team: { bg: 'bg-[var(--neo-yellow)]/20', border: 'border-[var(--neo-yellow)]' },
  };
  
  const colors = planColors[quota.plan] || planColors.free;
  
  // 역할 텍스트
  const getRoleText = () => {
    if (!isTeamPlan) return '';
    const role = team?.my_role;
    if (role === 'owner') return lang === 'ko' ? '오너(리드)' : 'Owner(Lead)';
    if (role === 'manager') return lang === 'ko' ? '매니저' : 'Manager';
    return lang === 'ko' ? '멤버' : 'Member';
  };

  return (
    <div 
      className={`${colors.bg} border-b-[3px] ${colors.border} px-4 py-4 animate-slideDown`}
      style={{
        animation: 'slideDown 0.2s ease-out',
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
      <div className="max-w-4xl mx-auto space-y-4">
        {/* 헤더: 플랜 이름 + 닫기/업그레이드 버튼 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`neo-badge text-xs px-2 py-0.5 ${
              quota.plan === 'team' 
                ? 'bg-[var(--neo-yellow)] text-black' 
                : quota.plan === 'pro' 
                  ? 'bg-[var(--neo-mint)] text-black' 
                  : quota.plan === 'max'
                    ? 'bg-[var(--neo-orange)] text-black'
                    : 'bg-white text-black'
            }`}>
              {quota.plan.toUpperCase()}
              {isTeamPlan && ` (${getRoleText()})`}
            </span>
            <span className="text-xs font-bold text-black/60 dark:text-white/60 uppercase">
              {t.dashboard?.currentPlan || (lang === 'ko' ? '현재 플랜' : 'Current Plan')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {quota.plan !== 'max' && quota.plan !== 'team' && !isTeamPlan && (
              <button
                onClick={onUpgrade}
                className="neo-btn bg-black text-[var(--neo-lime)] px-3 py-1.5 text-xs"
              >
                {t.dashboard?.upgradeCta || (lang === 'ko' ? '플랜 업그레이드' : 'Upgrade Plan')}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white p-1"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* 사용량 섹션 */}
        <div className="bg-white/50 dark:bg-white/10 border-2 border-black/20 dark:border-white/20 p-3 rounded">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-black/70 dark:text-white/80 uppercase">
              {t.dashboard?.monthlyEvaluations || (lang === 'ko' ? '월간 평가' : 'Monthly Evaluations')}
            </span>
            <span className={`text-lg font-black text-black dark:text-white ${isAtLimit ? 'text-red-600 dark:text-red-400' : ''}`}>
              {unlimited ? (
                <span className="bg-black dark:bg-[var(--neo-mint)] text-[var(--neo-lime)] dark:text-black px-2 py-0.5 text-xs">
                  ∞ {t.dashboard?.unlimited || (lang === 'ko' ? '무제한' : 'Unlimited')}
                </span>
              ) : (
                <>
                  <span className="text-xl">{used}</span>
                  <span className="text-sm text-black/50 dark:text-white/50"> / {limit}</span>
                </>
              )}
            </span>
          </div>
          
          {/* 프로그레스 바 */}
          {!unlimited && (
            <div className="h-3 bg-white dark:bg-white/20 border border-black/20 dark:border-white/20 overflow-hidden rounded-full">
              <div
                className={`h-full transition-all duration-500 ${
                  isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-[var(--neo-orange)]' : 'bg-black dark:bg-[var(--neo-mint)]'
                }`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          )}
          
          {!unlimited && (
            <div className="flex justify-between mt-1.5 text-[10px] font-bold text-black/50 dark:text-white/50">
              <span>{used} {t.dashboard?.used || (lang === 'ko' ? '사용' : 'used')}</span>
              <span>{limit - used} {t.dashboard?.remaining || (lang === 'ko' ? '남음' : 'remaining')}</span>
            </div>
          )}
        </div>
        
        {/* 플랜 기능 그리드 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {/* 테스트케이스 */}
          <FeatureChip
            icon="📋"
            label={t.dashboard?.testCasesPerEval || (lang === 'ko' ? '평가당 테스트케이스' : 'Test Cases/Eval')}
            value={`${lang === 'ko' ? '최대 ' : 'Max '}${limits.max_test_cases}${t.dashboard?.items || (lang === 'ko' ? '개' : '')}`}
            available={true}
          />
          
          {/* Planning 글자수 */}
          <FeatureChip
            icon="📝"
            label={t.dashboard?.planningChars || (lang === 'ko' ? 'Planning 글자 수' : 'Planning Chars')}
            value={`${limits.max_planning_chars.toLocaleString()}${t.dashboard?.chars || (lang === 'ko' ? '자' : '')}`}
            available={true}
          />
          
          {/* 히스토리 */}
          <FeatureChip
            icon="📚"
            label={t.dashboard?.historyStorage || (lang === 'ko' ? '히스토리 저장' : 'History Storage')}
            value={limits.history_limit > 0 
              ? (historyUsage 
                  ? `${historyUsage.used} / ${historyUsage.limit}` 
                  : (isLoadingHistory ? '...' : `${limits.history_limit}${lang === 'ko' ? '건' : ''}`))
              : (t.dashboard?.notAvailable || (lang === 'ko' ? '사용 불가' : 'N/A'))}
            available={limits.history_limit > 0}
          />
          
          {/* PDF 다운로드 */}
          <FeatureChip
            icon="📄"
            label={t.dashboard?.pdfDownload || (lang === 'ko' ? 'PDF 다운로드' : 'PDF Download')}
            value={limits.pdf_download 
              ? (t.dashboard?.available || (lang === 'ko' ? '사용 가능' : 'Available')) 
              : (t.dashboard?.notAvailable || (lang === 'ko' ? '사용 불가' : 'N/A'))}
            available={limits.pdf_download}
          />
        </div>
      </div>
    </div>
  );
}

// 간략한 기능 칩 컴포넌트
function FeatureChip({ 
  icon, 
  label, 
  value, 
  available = true
}: { 
  icon: string; 
  label: string; 
  value: string; 
  available?: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded border ${
      available 
        ? 'bg-white/30 dark:bg-white/5 border-black/10 dark:border-white/10' 
        : 'bg-gray-200/30 dark:bg-black/30 border-gray-300/30 dark:border-gray-600/30'
    }`}>
      <span className={`text-base ${!available ? 'grayscale opacity-40' : ''}`}>{icon}</span>
      <div className="flex-1 min-w-0">
        <span className={`text-[9px] font-bold block truncate ${
          available ? 'text-black/50 dark:text-white/50' : 'text-black/30 dark:text-white/30'
        }`}>
          {label}
        </span>
        <span className={`text-xs font-black block truncate ${
          available ? 'text-black dark:text-white' : 'text-black/30 dark:text-white/30 line-through'
        }`}>
          {value}
        </span>
      </div>
      {available && (
        <span className="w-1.5 h-1.5 bg-[var(--neo-mint)] rounded-full flex-shrink-0"></span>
      )}
    </div>
  );
}

